import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navItems = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeBubbleStyle, setActiveBubbleStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const navRef = useRef(null);
  const linkRefs = useRef({});
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const updateBubble = () => {
      const navEl = navRef.current;
      const activePath = navItems.find((item) => {
        if (item.path === "/") return location.pathname === "/";
        return location.pathname.startsWith(item.path);
      })?.path;
      const activeEl = activePath ? linkRefs.current[activePath] : null;
      if (!navEl || !activeEl) return;

      const navRect = navEl.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();

      setActiveBubbleStyle({
        left: activeRect.left - navRect.left,
        width: activeRect.width,
        opacity: 1
      });
    };

    const rafId = window.requestAnimationFrame(updateBubble);
    window.addEventListener("resize", updateBubble);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateBubble);
    };
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-25">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 transition-transform duration-500 hover:scale-[1.02]">
            <span className="font-display text-2xl lg:text-3xl font-semibold text-foreground tracking-tight transition-[letter-spacing] duration-500 hover:tracking-[0.12em]">
              ROSKAM
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav ref={navRef} className="hidden lg:flex items-center gap-1 relative p-1 rounded-md">
            <span
              aria-hidden="true"
              className="absolute top-1 bottom-1 rounded-md bg-secondary transition-[transform,width,opacity] duration-300 ease-out"
              style={{
                transform: `translateX(${activeBubbleStyle.left}px)`,
                width: `${activeBubbleStyle.width}px`,
                opacity: activeBubbleStyle.opacity
              }}
            />
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                ref={(node) => {
                  if (node) linkRefs.current[item.path] = node;
                }}
                aria-current={isActive(item.path) ? "page" : undefined}
                className={`relative z-[1] px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive(item.path)
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center gap-2">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-background">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between py-4 border-b border-border">
                    <span className="font-display text-xl font-semibold">ROSKAM</span>
                  </div>
                  <nav className="flex flex-col gap-1 py-6">
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        aria-current={isActive(item.path) ? "page" : undefined}
                        className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                          isActive(item.path)
                            ? "text-foreground bg-secondary"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
